import Phaser from 'phaser';
import type { MapModule, PixelBlocker, PropDef } from './types';
import { compileBlockers } from './types';
import { useClockStore } from '@/state/clockStore';

// Returns 0..1 where 1 = full night, 0 = full day. Smooth dusk/dawn ramps.
function nightIntensityNow(): number {
  const now = useClockStore.getState().getNow();
  if (!now) return 0;
  const t = now.hour + now.minute / 60;
  if (t >= 7 && t < 18) return 0;
  if (t >= 5 && t < 7) return Math.max(0, 1 - (t - 5) / 2);
  if (t >= 18 && t < 20) return (t - 18) / 2;
  return 1;
}

export interface RenderedMap {
  mod: MapModule;
  blockers: PixelBlocker[];
  images: Phaser.GameObjects.Image[];
  // Map of prop index → rendered image (parallel to mod.props). Editor uses
  // this to mutate live without re-creating the scene.
  propImages: Array<Phaser.GameObjects.Image | null>;
}

export interface RenderOptions {
  // Optional callback used when the map declares <Procedural theme="..."/>.
  // Lets the runtime delegate ground painting to the legacy
  // buildFieldTilemap() without importing it directly (avoids cycles).
  paintProcedural?: (scene: Phaser.Scene, theme: string) => void;
  // When true, draws a red border around every compiled collision blocker
  // so collision shapes are visible at runtime (debug aid).
  debugCollisions?: boolean;
}

// Apply a PropDef to an existing Phaser.Image (used by the editor for live
// preview after a draft mutation).
// Y-sort baseline: use the sprite's visual base (bottom edge) so taller props
// render behind actors standing in front of them. Props are drawn with the
// default origin (0.5, 0.5), so the base sits at y + h/2.
function defaultPropDepth(p: PropDef): number {
  return p.depth ?? (p.y + p.h / 2);
}

// Starts the tween (if any) declared by a prop on the given image. Mirrors
// the tween logic in spawnPropImages but without recreating the image, so
// the editor can react to live tween changes.
export function applyPropTween(
  scene: Phaser.Scene,
  img: Phaser.GameObjects.Image,
  p: PropDef,
): void {
  scene.tweens.killTweensOf(img);
  // Reset transient props that tweens mutate so killing leaves a clean state.
  img.setAlpha(p.alpha ?? 1);
  if (p.tween === 'flicker') {
    const TOP_LIGHT_PROPS: Record<string, number> = {
      prop_street_lantern: 0.42,
    };
    if (!TOP_LIGHT_PROPS[p.image]) {
      scene.tweens.add({
        targets: img, alpha: { from: 0.7, to: 1 }, duration: 420,
        yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    }
    // Note: the lantern glow overlay variant is created at spawn time only;
    // toggling flicker live on a lantern won't add the glow until reload.
  } else if (p.tween === 'breathe') {
    scene.tweens.add({
      targets: img, scale: { from: img.scale * 0.95, to: img.scale * 1.05 },
      duration: 1800, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
  }
}

export function applyPropToImage(img: Phaser.GameObjects.Image, p: PropDef): void {
  if (img.texture?.key !== p.image && img.scene?.textures.exists(p.image)) {
    img.setTexture(p.image);
  }
  img.setPosition(p.x, p.y);
  img.setDisplaySize(p.w, p.h);
  img.setDepth(defaultPropDepth(p));
  img.setTint(p.tint ?? 0xffffff);
  if (p.tint == null) img.clearTint();
  img.setFlipX(!!p.flipX);
  img.setFlipY(!!p.flipY);
  img.setAlpha(p.alpha ?? 1);
  img.setAngle(p.rotation ?? 0);
}

export function spawnPropImage(scene: Phaser.Scene, p: PropDef): Phaser.GameObjects.Image | null {
  const out = spawnPropImages(scene, p);
  return out ? out[0] : null;
}

// Returns all GameObjects created for a prop (main image + optional overlays
// such as the flicker light overlay for street lanterns). Caller should track
// every returned object for shutdown/cleanup.
export function spawnPropImages(
  scene: Phaser.Scene,
  p: PropDef,
): Phaser.GameObjects.Image[] | null {
  if (!scene.textures.exists(p.image)) return null;
  const img = scene.add.image(p.x, p.y, p.image).setDisplaySize(p.w, p.h);
  img.setDepth(defaultPropDepth(p));
  if (p.tint != null) img.setTint(p.tint);
  if (p.flipX) img.setFlipX(true);
  if (p.flipY) img.setFlipY(true);
  if (p.alpha != null) img.setAlpha(p.alpha);
  if (p.rotation != null) img.setAngle(p.rotation);
  const created: Phaser.GameObjects.Image[] = [img];
  if (p.tween === 'flicker') {
    // For tall lantern-style props, we don't want the entire pole/post to
    // flicker — only the lamp head at the top. Add a small soft glow
    // circle just BEHIND the lamp head (depth slightly less than the
    // lantern image) and tween its alpha. The lantern sprite itself stays
    // fully opaque, so it never disappears.
    const TOP_LIGHT_PROPS: Record<string, number> = {
      // key → fraction of source-image height occupied by the lamp head
      prop_street_lantern: 0.42,
    };
    const topFrac = TOP_LIGHT_PROPS[p.image];
    if (topFrac) {
      const lampCenterY = p.y - p.h / 2 + (p.h * topFrac) / 2;
      const glowRadius = Math.max(p.w, p.h * topFrac) * 0.7;
      // Outer soft halo — additive white glow that punches through the
      // night ambient MULTIPLY overlay (depth 9000). Sits ABOVE the
      // ambient layer so the night tint can't darken it away.
      const halo = scene.add.circle(p.x, lampCenterY, glowRadius, 0xffffff, 0.0);
      halo.setBlendMode(Phaser.BlendModes.ADD);
      halo.setDepth(9050);
      // Inner core glow — sits just behind the lantern sprite.
      const core = scene.add.circle(p.x, lampCenterY, glowRadius * 0.45, 0xffffff, 0.28);
      core.setDepth(defaultPropDepth(p) - 1);
      created.push(halo as unknown as Phaser.GameObjects.Image);
      created.push(core as unknown as Phaser.GameObjects.Image);
      const onUpdate = (): void => {
        const night = nightIntensityNow();
        halo.setFillStyle(0xffffff, 0.55 * night);
        const coreA = 0.18 + 0.35 * night;
        core.setFillStyle(0xffffff, coreA);
      };
      scene.events.on(Phaser.Scenes.Events.UPDATE, onUpdate);
      const cleanup = (): void => {
        scene.events.off(Phaser.Scenes.Events.UPDATE, onUpdate);
      };
      halo.once(Phaser.GameObjects.Events.DESTROY, cleanup);
      scene.events.once(Phaser.Scenes.Events.SHUTDOWN, cleanup);
      scene.events.once(Phaser.Scenes.Events.DESTROY, cleanup);
      // Lantern image itself stays static and fully opaque.
    } else {
      scene.tweens.add({
        targets: img, alpha: { from: 0.7, to: 1 }, duration: 420,
        yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    }
  } else if (p.tween === 'breathe') {
    scene.tweens.add({
      targets: img, scale: { from: img.scale * 0.95, to: img.scale * 1.05 },
      duration: 1800, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
  }
  return created;
}

// Paints base + props of a MapModule into the given scene. Returns the
// pixel-rect blockers the scene should feed into its movement/collision logic
// plus the spawned image objects (caller may keep handles for shutdown).
export function renderMapModule(
  scene: Phaser.Scene,
  mod: MapModule,
  opts: RenderOptions = {},
): RenderedMap {
  const images: Phaser.GameObjects.Image[] = [];
  const propImages: Array<Phaser.GameObjects.Image | null> = [];

  if (mod.procedural && opts.paintProcedural) {
    opts.paintProcedural(scene, mod.procedural.theme);
  }

  if (mod.base?.image && scene.textures.exists(mod.base.image)) {
    const bg = scene.add
      .image(mod.width / 2, mod.height / 2, mod.base.image)
      .setDisplaySize(mod.width, mod.height)
      .setDepth(0);
    images.push(bg);
  }

  for (const p of mod.props) {
    const objs = spawnPropImages(scene, p);
    propImages.push(objs ? objs[0] : null);
    if (objs) for (const o of objs) images.push(o);
  }

  const blockers = compileBlockers(mod);
  if (opts.debugCollisions) drawCollisionDebug(scene, blockers);

  return { mod, blockers, images, propImages };
}

// Draws a red border around each compiled blocker rect for debug visualization.
export function drawCollisionDebug(
  scene: Phaser.Scene,
  blockers: PixelBlocker[],
  color = 0xff0000,
  alpha = 1,
): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics();
  g.setDepth(100000);
  g.lineStyle(1, color, alpha);
  for (const b of blockers) g.strokeRect(b.x, b.y, b.w, b.h);
  return g;
}
